import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSiteImages } from "../context/SiteImagesContext";
import { useAuth } from "../context/AuthContext";
import { supabase, formatSupabaseError } from "../lib/supabase";
import {
  adminListFiles, adminUploadFile, adminDeleteFile, adminUpdateFileFolder,
  adminListSlots, adminAssignSlot,
  coursesCrud, playersCrud, staffCrud, matchesCrud, newsCrud,
  adminListEnrollments, adminListMessages, sponsorsCrud, gymEventsCrud,
  listGiocatoriGestionale, genericEventsCrud, gymSospensioniCrud,
  ritiriCrud, adminListPrenotazioniRitiri, adminUpdatePrenotazioneRitiroStatus, adminDeletePrenotazioneRitiro,
} from "../lib/db";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Pencil, Plus, ImageIcon } from "lucide-react";

/* ========== File library ========== */
function NewsletterSection() {
  const [subject, setSubject] = useState("");
  const [highlight, setHighlight] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [iscritti, setIscritti] = useState([]);

  useEffect(() => {
    supabase.from("newsletter").select("*").order("created_at", { ascending: false })
      .then(({ data }) => setIscritti(data || []));
  }, []);

  const eliminaIscritto = async (id) => {
    if (!confirm("Rimuovere questo iscritto?")) return;
    await supabase.from("newsletter").delete().eq("id", id);
    setIscritti((prev) => prev.filter((i) => i.id !== id));
    toast.success("Iscritto rimosso");
  };

  const handleSend = async () => {
    if (!subject || !message) { toast.error("Compila oggetto e messaggio"); return; }
    if (!confirm(`Inviare la newsletter a ${iscritti} iscritti?`)) return;
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-newsletter", {
        body: { subject, message, highlight },
      });
      if (error) throw error;
      toast.success(`Newsletter inviata a ${iscritti} iscritti!`);
      setSubject(""); setHighlight(""); setMessage("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl uppercase tracking-tight">Invia Newsletter</h3>
          <span className="text-xs text-zinc-500">{iscritti.length} iscritti</span>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Oggetto email</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="es. Novità di giugno" className="bg-zinc-950 border-zinc-800" />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Titolo in evidenza (opzionale)</Label>
          <Input value={highlight} onChange={(e) => setHighlight(e.target.value)} placeholder="es. Vittoria 3-0!" className="bg-zinc-950 border-zinc-800" />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Messaggio</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Scrivi il contenuto della newsletter..." className="bg-zinc-950 border-zinc-800" />
        </div>
        <Button onClick={handleSend} disabled={sending || !subject || !message} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold w-full">
          {sending ? "Invio in corso..." : `Invia a ${iscritti.length} iscritti`}
        </Button>
      </div>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h3 className="font-display font-bold text-xl uppercase tracking-tight">Lista Iscritti</h3>
        </div>
        {iscritti.length === 0 ? (
          <div className="px-6 py-12 text-center text-zinc-500 text-sm">Nessun iscritto.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {iscritti.map((i) => (
              <li key={i.id} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <span className="font-medium">{i.email}</span>
                  <span className="text-xs text-zinc-500 ml-3">{new Date(i.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                <button
                  onClick={() => eliminaIscritto(i.id)}
                  className="p-1.5 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FileLibrary({ files, onUpload, onDelete, onUpdateFolder }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [folder, setFolder] = useState("Generale");
  const [activeFolder, setActiveFolder] = useState("Tutte");

  const existingFolders = [...new Set(files.map((f) => f.folder || "Generale"))].sort();

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      await onUpload(f, label, folder);
      setLabel("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("File caricato");
    } catch (err) {
      toast.error(formatSupabaseError(err));
    } finally {
      setUploading(false);
    }
  };

  const filteredFiles = activeFolder === "Tutte" ? files : files.filter((f) => (f.folder || "Generale") === activeFolder);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <h3 className="font-display font-bold uppercase tracking-tight mb-4">Carica nuova immagine</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Etichetta (opzionale)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="bg-zinc-950 border-zinc-800"
            data-testid="admin-upload-label"
          />
          <Input
            placeholder="Cartella (es. Squadra 2026)"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            list="folder-suggestions"
            className="bg-zinc-950 border-zinc-800"
            data-testid="admin-upload-folder"
          />
          <datalist id="folder-suggestions">
            {existingFolders.map((f) => <option key={f} value={f} />)}
          </datalist>
          <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" id="admin-upload-input" data-testid="admin-upload-input" />
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold"
            data-testid="admin-upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" /> {uploading ? "Caricamento…" : "Carica immagine"}
          </Button>
        </div>
        <p className="text-xs text-zinc-500 mt-3">Le immagini vengono caricate direttamente su Supabase Storage (bucket pubblico).</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveFolder("Tutte")}
          className={`px-3 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${activeFolder === "Tutte" ? "bg-yellow-400 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}
        >
          Tutte ({files.length})
        </button>
        {existingFolders.map((f) => (
          <button
            key={f}
            onClick={() => setActiveFolder(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${activeFolder === f ? "bg-yellow-400 text-zinc-950" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}
          >
            {f} ({files.filter((x) => (x.folder || "Generale") === f).length})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredFiles.length === 0 && (
          <div className="col-span-full text-center py-12 rounded-2xl border border-dashed border-zinc-800">
            <ImageIcon className="h-8 w-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Nessuna immagine caricata.</p>
          </div>
        )}
        {filteredFiles.map((f) => (
          <div key={f.id} className="group rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800" data-testid={`file-${f.id}`}>
            <div className="aspect-square bg-zinc-950 overflow-hidden">
              <img src={f.public_url} alt={f.label} className="h-full w-full object-cover" />
            </div>
            <div className="p-3">
              <div className="text-xs font-semibold truncate" title={f.label}>{f.label}</div>
              <select
                value={f.folder || "Generale"}
                onChange={(e) => onUpdateFolder(f.id, e.target.value)}
                className="w-full mt-2 text-[11px] bg-zinc-950 border border-zinc-800 rounded-md px-2 py-1 text-zinc-300"
              >
                {existingFolders.map((folderName) => (
                  <option key={folderName} value={folderName}>{folderName}</option>
                ))}
              </select>
              <div className="flex items-center gap-1 mt-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(f.public_url); toast.success("URL copiato"); }}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-zinc-950 border border-zinc-800 hover:border-zinc-600 text-[11px] font-semibold transition-colors"
                  data-testid={`copy-${f.id}`}
                >
                  <Copy className="h-3 w-3" /> URL
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Eliminare ${f.label}?`)) return;
                    try { await onDelete(f); toast.success("Eliminato"); }
                    catch (e) { toast.error(formatSupabaseError(e)); }
                  }}
                  className="inline-flex items-center justify-center px-2 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300"
                  data-testid={`delete-file-${f.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ========== Slot mapping ========== */
function SlotMapping({ slots, files, onAssign }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-400 max-w-2xl">
        Associa un'immagine del bucket a ciascuna sezione del sito. Le modifiche sono immediate, senza toccare il codice.
      </p>
      {slots.map((s) => (
        <div key={s.key} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`slot-${s.key}`}>
          <div className="h-20 w-32 rounded-lg overflow-hidden bg-zinc-950 border border-zinc-800 shrink-0">
            <img src={s.public_url || s.default_url} alt="" className="h-full w-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-overline text-yellow-400 mb-1">{s.key}</div>
            <div className="font-display font-bold">{s.label}</div>
            <div className="text-xs text-zinc-500 mt-1">
              {s.public_url ? "Personalizzata (Supabase)" : "Default"}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:w-72">
            <Select
              value={s.file_id || "none"}
              onValueChange={(v) => onAssign(s.key, v === "none" ? null : files.find((f) => f.id === v))}
            >
              <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid={`slot-select-${s.key}`}>
                <SelectValue placeholder="Scegli immagine" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                <SelectItem value="none">— Default —</SelectItem>
                {files.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ========== Generic CRUD section ========== */
function CrudSection({ title, items, fields, onCreate, onUpdate, onDelete, files, renderRow, personeOptions, courseOptions }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const openCreate = () => {
    setEditing(null);
    setForm(Object.fromEntries(fields.map((f) => [f.name, f.type === "number" ? 0 : ""])));
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    const normalized = { ...item };
    if (typeof normalized.active === "boolean") {
      normalized.active = normalized.active ? "true" : "false";
    }
    setForm(normalized);
    setOpen(true);
  };
  const submit = async (e = { preventDefault: () => {} }) => {
    e.preventDefault();
    try {
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.name];
        if (f.type === "number") v = v === "" || v == null ? null : parseInt(v, 10);
        if (f.name === "active") v = v === "true";
        if (f.type === "multi-image-select" && !v) v = [];
        if (f.type === "multi-file-select" && !v) v = [];
        if (f.optional && f.type !== "multi-image-select" && f.type !== "multi-file-select" && (v === "" || v == null)) v = null;
        payload[f.name] = v;
      });
      if (editing) {
        await onUpdate(editing.id, payload);
        toast.success("Aggiornato");
      } else {
        await onCreate(payload);
        toast.success("Creato");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatSupabaseError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display font-bold text-xl uppercase tracking-tight">{title}</h3>
        <Button onClick={openCreate} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold" data-testid={`crud-create-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          <Plus className="h-4 w-4 mr-1" /> Nuovo
        </Button>
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        {items.length === 0 ? (
          <div className="px-6 py-12 text-center text-zinc-500 text-sm">Nessun elemento.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {items.map((it) => (
              <li key={it.id} className="px-5 py-4 flex items-center gap-4" data-testid={`crud-row-${it.id}`}>
                {renderRow(it)}
                <div className="flex gap-2 ml-auto">
                  <button onClick={() => openEdit(it)} className="p-2 rounded-md bg-zinc-950 border border-zinc-800 hover:border-zinc-600" data-testid={`edit-${it.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={async () => { if (confirm("Eliminare?")) { try { await onDelete(it.id); toast.success("Eliminato"); } catch (e) { toast.error(formatSupabaseError(e)); } } }} className="p-2 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300" data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="crud-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{editing ? "Modifica" : "Nuovo"} {title.toLowerCase()}</DialogTitle>
            <DialogDescription className="text-zinc-400">Compila i campi e salva.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {fields.map((f) => (
              <div key={f.name}>
                <Label className="text-zinc-300">{f.label}{f.optional && <span className="text-zinc-600"> (opzionale)</span>}</Label>
                {f.type === "textarea" || f.type === "textarea-lg" ? (
                  <Textarea
                    rows={f.type === "textarea-lg" ? 20 : 3}
                    value={form[f.name] || ""}
                    onChange={(e) => {
                      setForm({ ...form, [f.name]: e.target.value });
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    onFocus={(e) => {
                      e.target.style.height = "auto";
                      e.target.style.height = e.target.scrollHeight + "px";
                    }}
                    style={{ resize: "vertical", overflow: "hidden" }}
                    className="bg-zinc-950 border-zinc-800 mt-1.5"
                    data-testid={`field-${f.name}`}
                  />
                ) : f.type === "multi-image-select" ? (
                  <div className="mt-1.5 space-y-2">
                    <Select
                      value="__add"
                      onValueChange={(v) => {
                        if (v === "__add") return;
                        const current = form[f.name] || [];
                        if (!current.includes(v)) {
                          setForm({ ...form, [f.name]: [...current, v] });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800">
                        <SelectValue placeholder="Aggiungi immagine da libreria" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                        <SelectItem value="__add">— Scegli un'immagine —</SelectItem>
                        {(files || []).map((file) => (
                          <SelectItem key={file.id} value={file.public_url}>{file.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="grid grid-cols-3 gap-2">
                      {(form[f.name] || []).map((url, idx) => (
                        <div key={idx} className="relative group">
                          <img src={url} alt="" className="h-20 w-full object-cover rounded-md" />
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (form[f.name] || []).filter((_, i) => i !== idx);
                              setForm({ ...form, [f.name]: updated });
                            }}
                            className="absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-500">{(form[f.name] || []).length} immagini selezionate (max 5)</p>
                  </div>
                ) : f.type === "multi-file-select" ? (
                  <div className="mt-1.5 space-y-2">
                    <Select
                      value="__add"
                      onValueChange={(v) => {
                        if (v === "__add") return;
                        const file = (files || []).find((fl) => fl.public_url === v);
                        if (!file) return;
                        const current = form[f.name] || [];
                        if (!current.some((d) => d.url === file.public_url)) {
                          setForm({ ...form, [f.name]: [...current, { url: file.public_url, label: file.label || file.original_filename }] });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800">
                        <SelectValue placeholder="Aggiungi documento da libreria" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                        <SelectItem value="__add">— Scegli un file —</SelectItem>
                        {(files || []).map((file) => (
                          <SelectItem key={file.id} value={file.public_url}>{file.label || file.original_filename}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <ul className="space-y-1.5">
                      {(form[f.name] || []).map((doc, idx) => (
                        <li key={idx} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm">
                          <span className="truncate">{doc.label}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (form[f.name] || []).filter((_, i) => i !== idx);
                              setForm({ ...form, [f.name]: updated });
                            }}
                            className="text-red-400 hover:text-red-300 shrink-0"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-zinc-500">Carica prima i PDF nella tab "Immagini" (File library accetta qualsiasi file), poi selezionali qui.</p>
                  </div>
                ) : f.type === "image-select" ? (
                  <div className="mt-1.5 space-y-2">
                    <Select
                      value={form[f.name] || "__manual"}
                      onValueChange={(v) => setForm({ ...form, [f.name]: v === "__manual" ? form[f.name] || "" : v })}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800">
                        <SelectValue placeholder="Scegli da libreria" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                        <SelectItem value="__manual">— URL manuale —</SelectItem>
                        {(files || []).map((file) => (
                          <SelectItem key={file.id} value={file.public_url}>{file.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={form[f.name] || ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      placeholder="https://…"
                      className="bg-zinc-950 border-zinc-800"
                      data-testid={`field-${f.name}`}
                    />
                    {form[f.name] && <img src={form[f.name]} alt="" className="h-20 w-full object-cover rounded-md" />}
                  </div>
                ) : f.type === "persona-select" ? (
                  <Select
                    value={form[f.name] || "__none"}
                    onValueChange={(v) => setForm({ ...form, [f.name]: v === "__none" ? "" : v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid={`field-${f.name}`}>
                      <SelectValue placeholder="Collega a una persona del gestionale" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                      <SelectItem value="__none">— Nessun collegamento —</SelectItem>
                      {(personeOptions || []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.cognome} {p.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "course-select" ? (
                  <Select
                    value={form[f.name] || "__none"}
                    onValueChange={(v) => setForm({ ...form, [f.name]: v === "__none" ? "" : v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid={`field-${f.name}`}>
                      <SelectValue placeholder="Seleziona corso o tutta la palestra" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-72">
                      <SelectItem value="__none">— Tutta la palestra (chiusura totale) —</SelectItem>
                      {(courseOptions || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.day_of_week} {c.time_start}–{c.time_end} • {c.course_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid={`field-${f.name}`}>
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "checkbox" ? (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="checkbox"
                      checked={!!form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                      className="h-4 w-4 rounded border-zinc-700 bg-zinc-950 text-yellow-400 focus:ring-yellow-400"
                    />
                    <Label className="text-sm text-zinc-400 font-normal">{f.label}</Label>
                  </div>
                ) : f.type === "datetime-local" ? (
                  <Input
                    type="datetime-local"
                    value={form[f.name] ? (() => {
                      const d = new Date(form[f.name]);
                      const pad = (n) => String(n).padStart(2, "0");
                      const rome = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Rome" }));
                      return `${rome.getFullYear()}-${pad(rome.getMonth()+1)}-${pad(rome.getDate())}T${pad(rome.getHours())}:${pad(rome.getMinutes())}`;
                    })() : ""}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const [datePart, timePart] = e.target.value.split("T");
                      const [year, month, day] = datePart.split("-");
                      const [hour, minute] = timePart.split(":");
                      const tempDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00Z`);
                      const romeOffset = new Date(tempDate.toLocaleString("en-US", { timeZone: "Europe/Rome" })) - new Date(tempDate.toLocaleString("en-US", { timeZone: "UTC" }));
                      const finalDate = new Date(tempDate.getTime() - romeOffset);
                      setForm({ ...form, [f.name]: finalDate.toISOString() });
                    }}
                    className="bg-zinc-950 border-zinc-800 mt-1.5"
                    data-testid={`field-${f.name}`}
                  />
                ) : (
                  <Input
                    type={f.type || "text"}
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 mt-1.5"
                    data-testid={`field-${f.name}`}
                  />
                )}
              </div>
            ))}
            <DialogFooter className="pt-2">
              <Button type="button" onClick={submit} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold w-full" data-testid="crud-submit">
                Salva
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ========== Main Admin page ========== */
export default function Admin() {
  const navigate = useNavigate();
  const { session, loading: authLoading, user: authUser, profile } = useAuth();
  const { refresh: refreshSiteImages } = useSiteImages();
  console.log("[Admin] render", { authLoading, session: !!session, role: authUser?.role });

  const [phase, setPhase] = useState("checking");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileRole, setProfileRole] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const hasCheckedRef = useRef(false);

  const [files, setFiles] = useState([]);
  const [slots, setSlots] = useState([]);
  const [courses, setCourses] = useState([]);
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [matches, setMatches] = useState([]);
  const [news, setNews] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [settings, setSettings] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [gymEvents, setGymEvents] = useState([]);
  const [giocatoriGestionale, setGiocatoriGestionale] = useState([]);
  const [genericEvents, setGenericEvents] = useState([]);
  const [gymSospensioni, setGymSospensioni] = useState([]);
  const [ritiri, setRitiri] = useState([]);
  const [prenotazioniRitiri, setPrenotazioniRitiri] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [f, sl, c, p, st, m, n, en, ms, sett, sp, ge, gg, gen, gs, ri, pr] = await Promise.all([
        adminListFiles(),
        adminListSlots(),
        coursesCrud.list(),
        playersCrud.list(),
        staffCrud.list().then(items => items.sort((a, b) => (a.order || 999) - (b.order || 999))),
        matchesCrud.list(),
        newsCrud.list(),
        adminListEnrollments(),
        adminListMessages(),
        supabase.from("site_settings").select("*"),
        sponsorsCrud.list(),
        gymEventsCrud.list(),
        listGiocatoriGestionale().catch(() => []),
        genericEventsCrud.list(),
        gymSospensioniCrud.list(),
        ritiriCrud.list(),
        adminListPrenotazioniRitiri(),
      ]);
      setFiles(f || []);
      setSlots(sl || []);
      setCourses(c || []);
      setPlayers(p || []);
      setStaff(st || []);
      setMatches(m || []);
      setNews(n || []);
      setEnrollments(en || []);
      setMessages(ms || []);
      setSettings(sett?.data || []);
      setSponsors(sp || []);
      setGymEvents(ge || []);
      setGiocatoriGestionale(gg || []);
      setGenericEvents(gen || []);
      setGymSospensioni(gs || []);
      setRitiri(ri || []);
      setPrenotazioniRitiri(pr || []);
    } catch (err) {
      console.error("[Admin] loadAll error:", err);
      toast.error("Errore: " + formatSupabaseError(err));
      setErrorMsg(formatSupabaseError(err));
    }
  }, []);

  // Unico effect: aspetta che auth sia pronto, poi controlla il ruolo
  useEffect(() => {
    // Aspetta che AuthContext abbia finito di caricare
    if (authLoading) return;

    // Nessuna sessione → login
    if (!session) {
      navigate("/login");
      return;
    }

    // Sessione presente ma ruolo non ancora controllato
    if (!hasCheckedRef.current && authUser && authUser.role) {
      hasCheckedRef.current = true;
      const role = authUser?.role || "user";
      const email = authUser?.email || session.user?.email || "";
      setProfileEmail(email);
      setProfileRole(role);

      if (role !== "admin") {
        setPhase("not_admin");
        return;
      }

      loadAll().then(() => setPhase("ready")).catch((err) => {
        setErrorMsg(err.message || "Errore caricamento dati");
        setPhase("error");
      });
    }
  }, [authLoading, session, authUser, navigate, loadAll]);

  // ----- Render by phase -----
  if (authLoading || phase === "checking") {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="admin-loading">
        <div className="h-10 w-10 rounded-full border-2 border-yellow-400 border-t-transparent animate-spin" />
        <p className="text-zinc-500 font-display tracking-wider uppercase text-xs">Verifico autenticazione…</p>
      </div>
    );
  }

  if (phase === "no_session") return null;

  if (phase === "error") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20" data-testid="admin-error">
        <div className="text-overline text-yellow-400 mb-3">// Errore</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-4">Qualcosa non va</h1>
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 space-y-3 text-sm">
          <p className="text-red-300 font-mono break-words">{errorMsg}</p>
          <Button onClick={() => { hasCheckedRef.current = false; setPhase("checking"); }} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold">Riprova</Button>
        </div>
      </div>
    );
  }

  if (phase === "not_admin") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20" data-testid="admin-denied">
        <div className="text-overline text-yellow-400 mb-3">// Accesso negato</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tight mb-4">Non sei admin</h1>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 space-y-3 text-sm">
          <p className="text-zinc-300"><span className="text-zinc-500">Email:</span> {profileEmail}</p>
          <p className="text-zinc-300"><span className="text-zinc-500">Ruolo attuale:</span> <code className="text-yellow-400">{profileRole}</code></p>
          <div className="pt-3 space-y-2 text-zinc-400 text-xs">
            <p>Per diventare admin esegui in Supabase SQL Editor:</p>
            <pre className="bg-zinc-950 border border-zinc-800 rounded-md p-3 overflow-x-auto text-zinc-200">
{`update public.profiles set role = 'admin' where email = '${profileEmail}';`}
            </pre>
          </div>
          <div className="flex gap-2 pt-3">
            <Button onClick={() => { hasCheckedRef.current = false; setPhase("checking"); }} className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold" data-testid="refresh-profile">
              Ricarica profilo
            </Button>
            <Button onClick={() => navigate("/")} variant="outline" className="border-zinc-700">Torna al sito</Button>
          </div>
        </div>
      </div>
    );
  }

  // phase === "ready"
  const upload = async (file, label, folder) => {
    await adminUploadFile(file, label, folder);
    await loadAll();
  };

  const updateFileFolder = async (fileId, folder) => {
    await adminUpdateFileFolder(fileId, folder);
    await loadAll();
  };

  const deleteFile = async (fileRow) => {
    await adminDeleteFile(fileRow);
    await loadAll();
    await refreshSiteImages();
  };

  const assignSlot = async (key, fileRow) => {
    await adminAssignSlot(key, fileRow);
    toast.success("Slot aggiornato");
    await loadAll();
    await refreshSiteImages();
  };

  const handlers = (crud, reload) => ({
    onCreate: async (payload) => { await crud.create(payload); await reload(); },
    onUpdate: async (id, payload) => { await crud.update(id, payload); await reload(); },
    onDelete: async (id) => { await crud.remove(id); await reload(); },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-10">
        <div>
          <div className="text-overline text-yellow-400 mb-2">// Pannello admin</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">Gestione sito</h1>
        </div>
        <Button onClick={() => navigate("/")} variant="outline" className="border-zinc-700 hover:border-zinc-500" data-testid="admin-back">Torna al sito</Button>
      </div>

      <Tabs defaultValue="files" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 p-1 h-auto flex flex-wrap" data-testid="admin-tabs">
          {[
            ["files", "Immagini"],
            ["slots", "Mappatura"],
            ["settings", "Info Sito"],
            ["courses", "Corsi"],
            ["players", "Giocatori"],
            ["staff", "Staff"],
            ["matches", "Partite"],
            ["news", "News"],
            ["enrollments", "Iscrizioni"],
            ["messages", "Messaggi"],
            ["sponsors", "Sponsor"],
            ["gymevents", "Orari Palestra"],
            ["gymsospensioni", "Sospensioni Palestra"],
            ["genericevents", "Eventi Generici"],
            ["ritiri", "Ritiri Estivi"],
            ["ritipren", "Prenotazioni Ritiri"],
            ["newsletter", "Newsletter"],
          ].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="data-[state=active]:bg-yellow-400 data-[state=active]:text-zinc-950 px-4 py-2 font-display font-bold uppercase text-xs tracking-wider" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="files" className="mt-8">
          <FileLibrary files={files} onUpload={upload} onDelete={deleteFile} onUpdateFolder={updateFileFolder} />
        </TabsContent>

        <TabsContent value="slots" className="mt-8">
          <SlotMapping slots={slots} files={files} onAssign={assignSlot} />
        </TabsContent>

        <TabsContent value="courses" className="mt-8">
          <CrudSection
            title="Corsi palestra"
            items={courses}
            files={files}
            fields={[
              { name: "name", label: "Nome corso" },
              { name: "category", label: "Categoria", type: "select", options: ["Forza", "Cardio", "Mobilità", "Calcio"] },
              { name: "description", label: "Descrizione", type: "textarea" },
              { name: "instructor", label: "Istruttore" },
              { name: "schedule", label: "Orari" },
              { name: "duration_min", label: "Durata (min)", type: "number" },
              { name: "level", label: "Livello" },
              { name: "image_url", label: "Immagine", type: "image-select", optional: true },
            ]}
            {...handlers(coursesCrud, async () => setCourses(await coursesCrud.list()))}
            renderRow={(c) => (
              <>
                <div className="h-12 w-12 rounded-lg bg-zinc-950 overflow-hidden shrink-0">{c.image_url && <img src={c.image_url} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.category} • {c.schedule}</div>
                </div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="players" className="mt-8">
          <CrudSection
            title="Giocatori"
            items={[...players].sort((a, b) => {
              const ORDINE_RUOLI = { "Portiere": 1, "Difensore": 2, "Centrocampista": 3, "Attaccante": 4 };
              const ra = ORDINE_RUOLI[a.role] || 99;
              const rb = ORDINE_RUOLI[b.role] || 99;
              if (ra !== rb) return ra - rb;
              return (a.shirt_number || 999) - (b.shirt_number || 999);
            })}
            files={files}
            personeOptions={giocatoriGestionale}
            fields={[
              { name: "name", label: "Nome e cognome" },
              { name: "shirt_number", label: "Numero maglia", type: "number" },
              { name: "role", label: "Ruolo principale" },
              { name: "secondary_role", label: "Ruolo secondario", optional: true },
              { name: "age", label: "Età", type: "number" },
              { name: "birth_date", label: "Data di nascita", type: "date", optional: true },
              { name: "goals", label: "Gol", type: "number" },
              { name: "image", label: "Foto", type: "image-select", optional: true },
              { name: "height_cm", label: "Altezza (cm)", type: "number", optional: true },
              { name: "weight_kg", label: "Peso (kg)", type: "number", optional: true },
              { name: "preferred_foot", label: "Piede preferito", type: "select", options: ["Destro", "Sinistro", "Ambidestro"], optional: true },
              { name: "hobbies", label: "Pregi (qualità del giocatore)", type: "textarea", optional: true },
              { name: "favorite_player", label: "Giocatore a cui si ispira", optional: true },
              { name: "favorite_team", label: "Squadra del cuore", optional: true },
              { name: "bio", label: "Biografia / presentazione breve", type: "textarea", optional: true },
              { name: "persona_id", label: "Collega a persona (gestionale) — opzionale, per statistiche partite", type: "persona-select", optional: true },
            ]}
            {...handlers(playersCrud, async () => setPlayers(await playersCrud.list()))}
            renderRow={(p) => (
              <>
                <div className="h-12 w-12 rounded-full bg-zinc-950 overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">#{p.shirt_number} {p.name}</div>
                  <div className="text-xs text-zinc-500 flex items-center gap-2">
                    <span>{p.role} • {p.age} anni</span>
                    {p.persona_id ? (
                      <span className="text-emerald-400">🔗 collegato</span>
                    ) : (
                      <span className="text-zinc-600">⚠ non collegato</span>
                    )}
                  </div>
                </div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="staff" className="mt-8">
          <CrudSection
            title="Staff tecnico"
            items={staff}
            files={files}
            fields={[
              { name: "name", label: "Nome" },
              { name: "role", label: "Ruolo" },
              { name: "order", label: "Ordine (es. 1, 2, 3)", type: "number" },
              { name: "is_sportivo", label: "Area Sportiva", type: "checkbox" },
              { name: "is_amministrativo", label: "Area Amministrativa", type: "checkbox" },
              { name: "image", label: "Foto", type: "image-select", optional: true },
            ]}
            {...handlers(staffCrud, async () => setStaff(await staffCrud.list()))}
            renderRow={(s) => (
              <>
                <div className="h-12 w-12 rounded-full bg-zinc-950 overflow-hidden shrink-0">{s.image && <img src={s.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{s.name}</div>
                  <div className="text-xs text-zinc-500">{s.role} {s.is_sportivo && "(Sport)"} {s.is_amministrativo && "(Admin)"}</div>
                </div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="matches" className="mt-8">
          <CrudSection
            title="Calendario & risultati"
            items={matches}
            files={files}
            fields={[
              { name: "match_date", label: "Data e ora (ora italiana)", type: "datetime-local" },
              { name: "home", label: "Squadra casa" },
              { name: "away", label: "Squadra ospite" },
              { name: "competition", label: "Competizione (es. girone, coppa)" },
              { name: "match_type", label: "Tipo partita", type: "select", options: ["Campionato", "Amichevole", "Torneo (generico)"] },
              { name: "venue", label: "Luogo (Casa / Trasferta)" },
              { name: "status", label: "Stato", type: "select", options: ["scheduled", "played"] },
              { name: "home_score", label: "Gol casa", type: "number", optional: true },
              { name: "away_score", label: "Gol ospite", type: "number", optional: true },
            ]}
            {...handlers(matchesCrud, async () => setMatches(await matchesCrud.list()))}
            renderRow={(m) => (
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{m.home} vs {m.away}</div>
                <div className="text-xs text-zinc-500 truncate">{new Date(m.match_date).toLocaleString("it-IT")} • {m.competition} • {m.match_type} • {m.status}</div>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="news" className="mt-8">
          <CrudSection
            title="News"
            items={news}
            files={files}
            fields={[
              { name: "title", label: "Titolo" },
              { name: "category", label: "Categoria", type: "select", options: ["Squadra", "Palestra", "Giovanile", "Società"] },
              { name: "excerpt", label: "Estratto" },
              { name: "body", label: "Corpo articolo", type: "textarea-lg" },
              { name: "image", label: "Immagine", type: "image-select", optional: true },
              { name: "images", label: "Galleria foto", type: "multi-image-select", optional: true },
            ]}
            {...handlers(newsCrud, async () => setNews(await newsCrud.list()))}
            renderRow={(n) => (
              <>
                <div className="h-12 w-16 rounded-md bg-zinc-950 overflow-hidden shrink-0">{n.image && <img src={n.image} alt="" className="h-full w-full object-cover" />}</div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{n.title}</div>
                  <div className="text-xs text-zinc-500 truncate">{n.category} • {new Date(n.published_at).toLocaleDateString("it-IT")}</div>
                </div>
              </>
            )}
          />
        </TabsContent>

        <TabsContent value="enrollments" className="mt-8">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            {enrollments.length === 0 ? <div className="px-6 py-12 text-center text-zinc-500 text-sm">Nessuna richiesta.</div> : (
              <ul className="divide-y divide-zinc-800">
                {enrollments.map((e) => (
                  <li key={e.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`adm-enr-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold">{e.full_name} → {e.course_name}</div>
                      <div className="text-xs text-zinc-500">{e.email} • {e.phone} • {new Date(e.created_at).toLocaleString("it-IT")}</div>
                      {e.notes && <div className="text-xs text-zinc-400 mt-1">Note: {e.notes}</div>}
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-yellow-400/15 text-yellow-300 border-yellow-400/30">{e.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-8">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            {messages.length === 0 ? <div className="px-6 py-12 text-center text-zinc-500 text-sm">Nessun messaggio.</div> : (
              <ul className="divide-y divide-zinc-800">
                {messages.map((m) => (
                  <li key={m.id} className="px-5 py-4" data-testid={`adm-msg-${m.id}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="font-display font-bold">{m.subject}</div>
                      <div className="flex items-center gap-3">
                        <div className="text-xs text-zinc-500">{new Date(m.created_at).toLocaleString("it-IT")}</div>
                        <button
                          onClick={async () => {
                            if (confirm("Eliminare questo messaggio?")) {
                              await supabase.from("contact_messages").delete().eq("id", m.id);
                              setMessages((prev) => prev.filter((x) => x.id !== m.id));
                            }
                          }}
                          className="p-1.5 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 mb-2">{m.name} • {m.email}</div>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{m.message}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-8">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 space-y-6 max-w-2xl">
            <h3 className="font-display font-bold text-xl uppercase">Dati del sito</h3>
            {settings.map((s) => (
              <div key={s.key} className="space-y-1.5">
                <Label className="text-zinc-400">{s.label}</Label>
                <div className="flex gap-2">
                  <Input
                    value={s.value || ""}
                    onChange={(e) => setSettings(settings.map(item => item.key === s.key ? { ...item, value: e.target.value } : item))}
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <Button
                    onClick={async () => {
                      await supabase.from("site_settings").update({ value: s.value }).eq("key", s.key);
                      toast.success("Salvato!");
                    }}
                    className="bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-bold"
                  >
                    Salva
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sponsors" className="mt-8">
          <CrudSection
            title="Sponsor"
            items={sponsors}
            files={files}
            fields={[
              { name: "name", label: "Nome sponsor" },
              { name: "logo_url", label: "Logo", type: "image-select", optional: true },
              { name: "website_url", label: "Sito web (URL)", optional: true },
              { name: "telefono", label: "Telefono", optional: true },
              { name: "tier", label: "Pacchetto", type: "select", options: ["bronzo", "argento", "oro"] },
              { name: "data_inizio", label: "Data inizio", type: "date" },
              { name: "data_scadenza", label: "Data scadenza", type: "date" },
              { name: "sort_order", label: "Ordine visualizzazione", type: "number" },
              { name: "active", label: "Attivo", type: "select", options: ["true", "false"] },
            ]}
            {...handlers(sponsorsCrud, async () => setSponsors(await sponsorsCrud.list()))}
            renderRow={(s) => {
              const oggi = new Date();
              oggi.setHours(0, 0, 0, 0);
              const scadenza = s.data_scadenza ? new Date(s.data_scadenza) : null;
              const giorniRimanenti = scadenza ? Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24)) : null;
              const isScaduto = giorniRimanenti !== null && giorniRimanenti < 0;
              const isInScadenza = giorniRimanenti !== null && giorniRimanenti >= 0 && giorniRimanenti <= 30;

              return (
                <>
                  <div className="h-12 w-16 rounded-md bg-zinc-950 overflow-hidden shrink-0">
                    {s.logo_url && <img src={s.logo_url} alt="" className="h-full w-full object-contain p-1" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{s.name}</div>
                    <div className="text-xs text-zinc-500">{s.tier} • {s.active ? "Attivo" : "Disattivo"}{s.telefono ? ` • ${s.telefono}` : ""}</div>
                    {isScaduto && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                        ⚠ Scaduto
                      </span>
                    )}
                    {isInScadenza && (
                      <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-400/20 text-yellow-300 border border-yellow-400/30">
                        ⏳ Scade tra {giorniRimanenti} giorni
                      </span>
                    )}
                  </div>
                </>
              );
            }}
          />
        </TabsContent>

        <TabsContent value="gymevents" className="mt-8">
          <CrudSection
            title="Orari palestra"
            items={gymEvents}
            files={files}
            fields={[
              { name: "course_name", label: "Nome corso" },
              { name: "day_of_week", label: "Giorno", type: "select", options: ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"] },
              { name: "day_order", label: "Ordine giorno (1=Lun, 7=Dom)", type: "number" },
              { name: "time_start", label: "Ora inizio (es. 09:00)" },
              { name: "time_end", label: "Ora fine (es. 10:00)" },
              { name: "instructor", label: "Istruttore" },
              { name: "active", label: "Visibile", type: "select", options: ["true", "false"] },
            ]}
            {...handlers(gymEventsCrud, async () => setGymEvents(await gymEventsCrud.list()))}
            renderRow={(e) => (
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{e.course_name}</div>
                <div className="text-xs text-zinc-500">{e.day_of_week} • {e.time_start}–{e.time_end} • {e.instructor}</div>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="gymsospensioni" className="mt-8">
          <p className="text-sm text-zinc-400 max-w-2xl mb-5">
            Sospendi un singolo corso in una data specifica (es. l'istruttore è in ferie) oppure chiudi
            l'intera palestra per un giorno (es. festività). L'evento sospeso non comparirà più nel
            calendario pubblico per quella data.
          </p>
          <CrudSection
            title="Sospensioni palestra"
            items={gymSospensioni}
            files={files}
            courseOptions={gymEvents}
            fields={[
              { name: "date", label: "Data", type: "date" },
              { name: "gym_event_id", label: "Corso da sospendere", type: "course-select", optional: true },
              { name: "motivo", label: "Motivo (es. Ferie, festività)", optional: true },
            ]}
            {...handlers(gymSospensioniCrud, async () => setGymSospensioni(await gymSospensioniCrud.list()))}
            renderRow={(s) => {
              const corso = gymEvents.find((g) => g.id === s.gym_event_id);
              return (
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold truncate">
                    {corso ? corso.course_name : "Tutta la palestra"}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">
                    {new Date(s.date + "T00:00:00").toLocaleDateString("it-IT")}
                    {s.motivo ? ` • ${s.motivo}` : ""}
                  </div>
                </div>
              );
            }}
          />
        </TabsContent>

        <TabsContent value="genericevents" className="mt-8">
          <CrudSection
            title="Eventi generici"
            items={genericEvents}
            files={files}
            fields={[
              { name: "title", label: "Titolo evento" },
              { name: "description", label: "Descrizione", type: "textarea", optional: true },
              { name: "event_date", label: "Data", type: "date" },
              { name: "time_start", label: "Ora inizio (es. 18:00)", optional: true },
              { name: "time_end", label: "Ora fine (es. 20:00)", optional: true },
              { name: "location", label: "Luogo", optional: true },
              { name: "active", label: "Visibile", type: "select", options: ["true", "false"] },
            ]}
            {...handlers(genericEventsCrud, async () => setGenericEvents(await genericEventsCrud.list()))}
            renderRow={(e) => (
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold truncate">{e.title}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {new Date(e.event_date + "T00:00:00").toLocaleDateString("it-IT")}
                  {e.time_start ? ` • ${e.time_start}` : ""}
                  {e.location ? ` • ${e.location}` : ""}
                </div>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="ritiri" className="mt-8">
          <CrudSection
            title="Ritiri estivi"
            items={ritiri}
            files={files}
            fields={[
              { name: "title", label: "Titolo ritiro" },
              { name: "team", label: "Squadra / categoria (es. Under 12, Prima squadra)", optional: true },
              { name: "description", label: "Descrizione", type: "textarea" },
              { name: "location", label: "Luogo", optional: true },
              { name: "start_date", label: "Data inizio", type: "date" },
              { name: "end_date", label: "Data fine", type: "date", optional: true },
              { name: "price", label: "Prezzo (€)", type: "number", optional: true },
              { name: "max_partecipanti", label: "Posti disponibili", type: "number", optional: true },
              { name: "photos", label: "Foto", type: "multi-image-select" },
              { name: "documents", label: "Documenti scaricabili (moduli, regolamento...)", type: "multi-file-select" },
              { name: "active", label: "Visibile sul sito", type: "select", options: ["true", "false"] },
            ]}
            {...handlers(ritiriCrud, async () => setRitiri(await ritiriCrud.list()))}
            renderRow={(r) => (
              <div className="min-w-0 flex-1 flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-zinc-950 overflow-hidden shrink-0">
                  {r.photos?.[0] && <img src={r.photos[0]} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{r.title}{r.team ? ` — ${r.team}` : ""}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {new Date(r.start_date + "T00:00:00").toLocaleDateString("it-IT")}
                    {r.end_date ? ` → ${new Date(r.end_date + "T00:00:00").toLocaleDateString("it-IT")}` : ""}
                    {r.location ? ` • ${r.location}` : ""}
                    {r.active === false ? " • nascosto" : ""}
                  </div>
                </div>
              </div>
            )}
          />
        </TabsContent>

        <TabsContent value="ritipren" className="mt-8">
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
            {prenotazioniRitiri.length === 0 ? (
              <div className="px-6 py-12 text-center text-zinc-500 text-sm">Nessuna prenotazione ricevuta.</div>
            ) : (
              <ul className="divide-y divide-zinc-800">
                {prenotazioniRitiri.map((p) => (
                  <li key={p.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`adm-ritipren-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold">
                        {p.full_name} → {p.ritiro?.title || "Ritiro eliminato"}
                        {p.ragazzo_nome ? ` (per ${p.ragazzo_nome})` : ""}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {p.email} • {p.phone} • {p.num_partecipanti} partecipant{p.num_partecipanti === 1 ? "e" : "i"} • {new Date(p.created_at).toLocaleString("it-IT")}
                      </div>
                      {p.notes && <div className="text-xs text-zinc-400 mt-1">Note: {p.notes}</div>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select
                        value={p.status}
                        onValueChange={async (status) => {
                          try {
                            await adminUpdatePrenotazioneRitiroStatus(p.id, status);
                            setPrenotazioniRitiri((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
                            toast.success("Stato aggiornato");
                          } catch (err) {
                            toast.error(formatSupabaseError(err));
                          }
                        }}
                      >
                        <SelectTrigger className={`w-[130px] h-8 text-xs border ${
                          p.status === "confirmed" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" :
                          p.status === "cancelled" ? "bg-red-400/15 text-red-300 border-red-400/30" :
                          "bg-yellow-400/15 text-yellow-300 border-yellow-400/30"
                        }`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                          <SelectItem value="pending">In attesa</SelectItem>
                          <SelectItem value="confirmed">Confermata</SelectItem>
                          <SelectItem value="cancelled">Annullata</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={async () => {
                          if (!confirm("Eliminare questa prenotazione?")) return;
                          await adminDeletePrenotazioneRitiro(p.id);
                          setPrenotazioniRitiri((prev) => prev.filter((x) => x.id !== p.id));
                          toast.success("Eliminata");
                        }}
                        className="p-1.5 rounded-md bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="newsletter" className="mt-8">
          <NewsletterSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}